<script setup lang="ts">
import type { SelectOption } from 'naive-ui'

type SelectSize = 'tiny' | 'small' | 'medium' | 'large'
const props = defineProps<{
  context: {
    value: string | string[]
    node: { input: (v: string | (string | number | undefined)[]) => void }
    update?: (v: string | (string | number | undefined)[]) => void
    multiSelectOptions: SelectOption[]
    placeholder?: string
    isMultiSelectAllowed?: boolean
    isOptionCreationAllowed?: boolean
    isClearable?: boolean
    hasSelectAll?: boolean
    hint?: string
    disabled?: boolean
    size?: SelectSize
    isLoading?: boolean
  }
}>()

const update = (event: string | (string | number | undefined)[]) => {
  if (props.context.update) {
    props.context.update(event)
  } else {
    props.context.node.input(event)
  }
}

// Remove duplicates
const options = computed(() => props.context.multiSelectOptions.filter((item, pos, self) => {
  return pos === self.findIndex(t => t.value === item.value)
}))

const value = computed(() => {
  if (!props.context.value) {
    return null
  }
  return props.context.value
})

const selectAll = () => update(props.context.multiSelectOptions.map(option => option.value))

const advancedFilterSearch = (pattern: string, option: SelectOption) => {
  const searchIn = []

  if (typeof option.label === 'string') {
    searchIn.push(option.label.toLowerCase())
  }
  if (typeof option.label === 'function') {
    const renderedLabel = option.label(option, false) as VNode
    const label = (renderedLabel.children as string[])[1]
    if (label) {
      searchIn.push(label.toLowerCase())
    }
  }

  return searchIn.some(search => search.includes(pattern.toLowerCase()) || search === pattern.toLowerCase())
}
</script>

<template>
  <div>
    <n-select
      :value="value"
      :options="options"
      :placeholder="context.placeholder ?? 'Wählen Sie aus der Liste'"
      :multiple="context.isMultiSelectAllowed ?? true"
      :consistent-menu-width="false"
      :tag="context.isOptionCreationAllowed"
      :clearable="context.isClearable"
      :disabled="context.disabled"
      filterable
      :filter="advancedFilterSearch"
      :size="context.size ?? 'large'"
      :loading="context.isLoading"
      @update:value="update"
    >
      <template v-if="context.hasSelectAll" #action>
        <n-button style="width: 100%" @click="selectAll">
          {{ $t('button.selectAll') }}
        </n-button>
      </template>
    </n-select>

    <p v-if="context.hint" class="text-xs pt-0.5 text-gray-400">
      {{ context.hint }}
    </p>
  </div>
</template>
